import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, from, of, throwError } from 'rxjs';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AuthService } from './auth.service';
import { catchError, map, mergeMap, skipWhile } from 'rxjs/operators'
import { ContextMembership, FirestoreUser, NetworkConstants, deepCopy, diffObject, removeUndefined } from 'diwimamodelweb';
import { TransactionLogService } from './transaction-log.service';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { UserFeedbackService } from './user-feedback.service';
import { Firestore, collection, deleteDoc, doc, getDoc, getDocs, updateDoc } from '@angular/fire/firestore';
import { anyToUser, updatesToFirestoreUpdates } from './utils/utils';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private Ifirestore = inject(Firestore);
  user$: Observable<any>
  // contextIds$: Observable<string[]>
  allUsersSubject$= new BehaviorSubject<FirestoreUser[]>(undefined)

  editUserContextMembership$ = new BehaviorSubject<any>(undefined)

  constructor(
    private authService: AuthService,
    private firestore: AngularFirestore,
    private transactionLogService: TransactionLogService,
    private functions: AngularFireFunctions,
    private userFeedback : UserFeedbackService
  ) {
    this.user$ = this.authService.user$.pipe(
      skipWhile(el => !el),
      map(user => user?.uid),
      mergeMap(userId => {
        this.userFeedback.loading.emit(true)
        if (!userId) return throwError(() => new Error("No uid specified. User data corrupted?"))
        // return this.firestore.doc(`${NetworkConstants.COLLECTION_USER}/${userId}`).valueChanges()
        // try{
        // return this.firestore.doc(`User/${userId}`).get().pipe(
        //   mergeMap(user => {
        //     if (user.exists) return of(user.data())
        //     else throwError(() => new Error('No corrsponding user in firestore'))
        //   })
        // )
        // }catch(error ) {console.log(error)}  

        const docRef = doc(this.Ifirestore, `User/${userId}`)
        return from(getDoc(docRef))
        .pipe(
          mergeMap(user => {
            this.userFeedback.loading.emit(false)

            if (user.exists()) return of(user.data())
            else throwError(() => new Error('No corrsponding user in firestore'))
            // this.userFeedback.loading.emit(false)
          }),
          catchError(err => {
            console.log( err);
            return throwError(() => err); // rethrow or handle
          })
        )
      })
    )
  }

  deleteUser(user: FirestoreUser): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const userToDelete = deepCopy(user)
      const dataToDelete = removeUndefined(userToDelete)
      const transactionLogItem = this.transactionLogService.deleteDocumentLog(
        user,
        user.identifier,
        NetworkConstants.COLLECTION_USER,
        dataToDelete
      )
      const promiseArray = []
      promiseArray.push(this.transactionLogService.saveTransactionLog([transactionLogItem]))
      
      // const delOperation = this.firestore.collection(NetworkConstants.COLLECTION_USER).doc(user.identifier).delete()
      const docRef = doc(this.Ifirestore, NetworkConstants.COLLECTION_USER, user.identifier)
      const delOperation = await deleteDoc( docRef)

      promiseArray.push(delOperation)
      try {
        await Promise.all(promiseArray)
        resolve()
      } catch (error) {
        reject(error)
      }
    })
  }

  async removeUser(user:FirestoreUser, contextId): Promise<any>{
    return new Promise(async (resolve, reject) => {

      try{
        this.userFeedback.loading.emit(true)
        const callable = this.functions.httpsCallable('admin-removeUser')
        let data = {
          contextId: contextId,
          userId: user.identifier
        }
        const reply: any = await callable(data).toPromise().then(value => {
          if(value)
          resolve(value)
          this.userFeedback.loading.emit(false)

          return value
        })
      }catch(error){
        reject(error)
      }
    })

  }

  observeUserContextMembershipOfSelectedContext(user:FirestoreUser, selectedContextId):  Promise<any> {
    return new Promise(async (resolve, reject) => {

      try{
        const collRef = collection(this.Ifirestore, `${NetworkConstants.COLLECTION_USER}/${user.identifier}/${NetworkConstants.COLLECTION_CONTEXT_MEMBERSHIP}`)

        const results = await getDocs(collRef)
        if (!results.empty) {
          await new Promise((r, rej) => {
            results.docs.forEach(async (element) => {
              if(element.id == selectedContextId){
                const data = element.data()
                const context = new ContextMembership()
                context.setFromAny(data)
                this.editUserContextMembership$.next(context)
                resolve(context)
              }
            });
          })
        }else{
          reject()
        }
      }catch(error) {
        reject(error)
      }
    })

  }

  /** Self-service profile edit — basic details + per-context notification prefs, no roles/rights. */
  async updateOwnUser(oldUser: any, patch: Record<string, any>): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        const newUser = anyToUser({ ...oldUser, ...patch })
        const updates = diffObject(oldUser, newUser, {})
        const map = updatesToFirestoreUpdates(updates)
        const list = await this.transactionLogService.determineDiff(oldUser, updates, oldUser.identifier, NetworkConstants.COLLECTION_USER)
        map.updateTimestamp = new Date()
        const docRef = doc(this.Ifirestore, NetworkConstants.COLLECTION_USER, oldUser.identifier)
        await updateDoc(docRef, map)
        await this.transactionLogService.saveTransactionLog(list)
        resolve()
      } catch (error) {
        reject(error)
      }
    })
  }

  async setUserSelectedContextId(user, cId: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
    
      try {
        const newUser = anyToUser(user)
        newUser.selectedContextId = cId
        const updates = diffObject(user, newUser, {})
        const map = updatesToFirestoreUpdates(updates)
        const list = await this.transactionLogService.determineDiff(user, updates, newUser.identifier, NetworkConstants.COLLECTION_USER)
        // save user data ...
        map.updateTimestamp = new Date()
        const docRef = doc(this.Ifirestore, NetworkConstants.COLLECTION_USER, newUser.identifier)
        await updateDoc( docRef, map)
        // save transactionlog data...
        await this.transactionLogService.saveTransactionLog(list)
        resolve()
      } catch (error) {
        reject(error)
      }
    })
  }
}
